"use client";

import { useEffect, useRef, useState } from "react";

import { useRouter, useSearchParams } from "next/navigation";

import { trackVerified } from "../../_components/meta-pixel";
import { APP_STORE_URL, PLAY_STORE_URL, SUPPORT_EMAIL, applicationFormUrl } from "../_constants";
import { SignInNote } from "./sign-in-note";

type Phase = "loading" | "verify" | "expert_complete" | "client_conflict" | "no_reference";

/**
 * Screen 1 of the web onboarding flow: phone verification.
 *
 * Collecting the applicant's details belongs to the marketing team's Application
 * Form, which redirects here with a single-use reference. This screen exchanges
 * that reference for a code and takes it from there — nothing personal ever
 * appears in the URL.
 */
/**
 * Step label, heading and intro for this screen, per phase.
 *
 * Kept with the phase rather than in the page: a static heading promising a code
 * has been sent sits badly above a card saying the application could not be
 * found.
 */
function heading(phase: Phase, disciplineLabel: string) {
  switch (phase) {
    case "no_reference":
      return {
        step: null,
        title: "We couldn't find your application.",
        intro: null,
      };
    case "expert_complete":
    case "client_conflict":
      return { step: null, title: "One moment.", intro: null };
    default:
      return {
        step: "Step 1 of 3",
        title: "Let's verify it's you.",
        intro: `We've sent a code to the number from your application. You'll finish setting up as a ${disciplineLabel} in the next couple of steps.`,
      };
  }
}

export default function VerifyForm({
  slug,
  disciplineLabel,
}: {
  slug: string;
  disciplineLabel: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const reference = searchParams?.get("ref") ?? null;

  const [phase, setPhase] = useState<Phase>("loading");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState("");

  // React runs effects twice in development. Without this the reference is used
  // by the first pass and the second races it — the flow would look broken
  // locally and work in production, which is the worst way round.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Off the URL before anything else runs. The reference is a bearer token for
    // this applicant's PII, and every analytics script on the page reports the
    // full URL — see the note in meta-pixel.tsx. Also stops a Back navigation
    // landing on a spent reference.
    if (reference && typeof window !== "undefined") {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("ref");
      window.history.replaceState(null, "", clean.toString());
    }

    fetch("/api/join/session")
      .then((r) => r.json())
      .then(
        (s: {
          phase: string;
          reference?: string | null;
          details?: { phoneCountryCode: string; phoneNumber: string };
        }) => {
          // An existing flow cookie normally wins: the code has already been
          // sent, and re-resolving would spend a second SMS. It does NOT win when
          // it belongs to a different reference — an applicant who corrected a
          // typo on the partner's form and came back would otherwise be shown the
          // old number and be unable to reach the new one for seven minutes.
          const cookieMatchesThisLink = !reference || !s.reference || s.reference === reference;

          if (s.phase === "verify" && s.details && cookieMatchesThisLink) {
            setMaskedPhone(`${s.details.phoneCountryCode} ${s.details.phoneNumber}`);
            setPhase("verify");
            return;
          }

          if (!reference) {
            // No link and no session. They may still be an applicant who pressed
            // Back after verifying, in which case they have an account and simply
            // need the next screen rather than an error.
            return resumeOrGiveUp();
          }

          return resolveReference(reference);
        },
      )
      .catch(() => {
        // Only reachable if /api/join/session itself failed. Reporting it beats
        // leaving the applicant on a blank card with no way to know anything
        // went wrong; the reference has not been used, so a reload recovers.
        setError("We couldn't load your application. Please refresh to try again.");
        setPhase("no_reference");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Distinguishes "never applied" from "already verified and came back".
   *
   * Pressing Back after verification lands here with no session cookie but a live
   * account, and telling that applicant their link is dead would be both wrong
   * and alarming.
   */
  async function resumeOrGiveUp() {
    try {
      const res = await fetch("/api/join/progress");
      if (res.ok) {
        router.replace(`/join/${slug}/profile`);
        return;
      }
    } catch {
      // Fall through — treated as not signed in.
    }
    setPhase("no_reference");
  }

  async function resolveReference(ref: string) {
    try {
      const res = await fetch("/api/join/resolve", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ reference: ref }),
      });

      const data = (await res.json()) as { status?: string; message?: string; phone?: string };

      if (!res.ok) {
        setError(
          res.status === 429
            ? "Too many attempts. Please wait a minute and try again."
            : (data.message ?? "This link has expired. Please apply again."),
        );
        setPhase("no_reference");
        return;
      }

      if (data.status === "expert_complete" || data.status === "client_conflict") {
        setPhase(data.status);
        return;
      }

      setMaskedPhone(data.phone ?? "");
      setPhase("verify");
    } catch {
      setError("Something went wrong. Please try again.");
      setPhase("no_reference");
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code we sent you.");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch("/api/join/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ otp_code: code.trim() }),
      });

      const data = (await res.json()) as { message?: string };

      if (!res.ok) {
        setError(
          res.status === 440
            ? "That took a while — please start again from the application form."
            : (data.message ?? "That code didn't work. Please try again."),
        );
        if (res.status === 440) {
          setPhase("no_reference");
          setCode("");
        }
        return;
      }

      // The conversion the marketing team optimises against — a verified number,
      // not a form submit. Fired before navigating so the pixel request leaves
      // while this page is still alive.
      trackVerified();

      router.push(`/join/${slug}/profile`);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/join/resend", { method: "POST" });
      if (!res.ok) setError("We couldn't resend the code. Please start again.");
    } finally {
      setBusy(false);
    }
  }

  const { step, title, intro } = heading(phase, disciplineLabel);

  const Header = () => (
    <>
      {step && (
        <p
          className="text-medium-sm mb-3 font-semibold tracking-wide uppercase"
          style={{ color: "var(--orange)" }}>
          {step}
        </p>
      )}
      <h1 className="mb-2 text-[2rem] leading-[1.15]">{title}</h1>
      {intro && (
        <p className="mb-7 text-[1.02rem]" style={{ color: "var(--ink-2)" }}>
          {intro}
        </p>
      )}
    </>
  );

  if (phase === "loading") {
    return <div className="join-card p-6" aria-busy="true" />;
  }

  if (phase === "no_reference") {
    return (
      <>
        <Header />
        <div className="join-card p-6">
          <p className="mb-5 text-[0.98rem]" style={{ color: "var(--ink-2)" }}>
            {error ??
              "This page opens from the application form. Apply there and you'll land straight back here."}
          </p>
          <a className="join-btn mb-4 block text-center" href={applicationFormUrl(slug)}>
            Go to the application form
          </a>
          <p className="join-hint">
            Still stuck? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and the team
            will sort it out.
          </p>
        </div>
      </>
    );
  }

  if (phase === "expert_complete" || phase === "client_conflict") {
    return (
      <>
        <Header />
        <div className="join-card p-6">
          <h2 className="mb-2 text-[1.35rem]">
            {phase === "expert_complete"
              ? "You're already set up"
              : "This number is already in use"}
          </h2>
          <p className="mb-5 text-[0.98rem]" style={{ color: "var(--ink-2)" }}>
            {phase === "expert_complete"
              ? "That number already has a JFit expert account. Open the app to pick up where you left off."
              : `That number is registered as a client account. Email ${SUPPORT_EMAIL} and the team will get you set up as an expert.`}
          </p>
          {phase === "expert_complete" && (
            <>
              <div className="mb-5 flex flex-col gap-2 sm:flex-row">
                <a
                  className="join-btn text-center"
                  href={APP_STORE_URL}
                  rel="noopener"
                  target="_blank">
                  App Store
                </a>
                <a
                  className="join-btn text-center"
                  href={PLAY_STORE_URL}
                  rel="noopener"
                  target="_blank">
                  Google Play
                </a>
              </div>
              {/*
                This branch is the one most at risk: the applicant already has a
                finished account, so signing in with a mismatched Google or Apple
                address here is exactly what produces the duplicate.
              */}
              <SignInNote />
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <form className="join-card grid gap-4 p-6" onSubmit={submitCode}>
        <div>
          <p className="text-[0.98rem]" style={{ color: "var(--ink-2)" }}>
            We sent a 6-digit code to
          </p>
          <p className="text-[1.05rem] font-semibold">{maskedPhone}</p>
        </div>

        <div>
          <label className="join-label" htmlFor="otp">
            Verification code
          </label>
          <input
            autoComplete="one-time-code"
            className="join-input tracking-[0.4em]"
            id="otp"
            inputMode="numeric"
            maxLength={6}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            value={code}
          />
        </div>

        {error && <div className="join-error">{error}</div>}

        <button className="join-btn" disabled={busy} type="submit">
          {busy ? "Verifying…" : "Verify and continue"}
        </button>

        <div className="flex justify-end">
          <button className="join-hint underline" disabled={busy} onClick={resend} type="button">
            Resend code
          </button>
        </div>
      </form>
    </>
  );
}
